import { Tab, Tabs, Tooltip } from '@mui/material';
import Text from './Atoms/Text';
import { Icon, IconType } from './Atoms/Icon';
import { SalesforceLinks } from './Compounds/SalesforceLinks/SalesforceLinks';
import { CopyButton } from './Compounds/CopyButton/CopyButton';
import { InternalLinks, InternalLink } from './Compounds/InternalLinks/InternalLinks';
import { Link } from './Compounds/DetailsDrawer/detailsDrawersHelpers';

export interface PageLayoutProps {
  pageTitle?: string;
  children?: React.ReactNode;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  pageActions?: React.ReactElement<any> | React.ReactElement<any>[];
  enableActions?: boolean;
  showTitle?: boolean;
  className?: string;
  width?: string;
  actionLocation?: string;
  dataCy?: string;
  dataTestId?: string;
  showSubTitle?: boolean;
  subTitle?: string;
  showLink?: boolean;
  showLinkGroup?: boolean;
  link?: string;
  linkGroup?: Link[];
  showInternalLinks?: boolean;
  internalLinks?: InternalLink[];
  showIcon?: boolean;
  icon?: IconType;
  tooltipLinkText?: string;
  showArchived?: boolean;
  showCopy?: boolean;
  copyValue?: string;
  tabs?: string[];
  handleTabChange?: (tabIndex: number) => void;
  activeTab?: number;
}

export const PageLayout = ({
  pageTitle,
  children,
  pageActions,
  enableActions = true,
  className,
  width = '',
  actionLocation = 'top',
  showTitle = true,
  dataCy,
  dataTestId,
  showSubTitle = true,
  subTitle,
  showLink,
  showLinkGroup = false,
  link = '',
  linkGroup = [],
  showInternalLinks = false,
  internalLinks = [],
  showIcon,
  icon,
  tooltipLinkText = 'Open in SF',
  showArchived = false,
  showCopy = false,
  copyValue = '',
  tabs,
  handleTabChange,
  activeTab
}: PageLayoutProps) => {
  // Filter internal links to only show visible ones, and only show the component if there are any visible links
  const visibleInternalLinks = internalLinks.filter((link) => link.visible !== false);
  const shouldShowInternalLinks = showInternalLinks && visibleInternalLinks.length > 0;
  return (
    <>
      <title>{`Tally ${pageTitle ? ` - ${pageTitle}` : ''}`}</title>
      <div
        data-cy={dataCy || ''}
        data-testid={dataTestId || ''}
        className={`w-[96vw] px-6 m-auto ${!pageTitle ? 'mt-10' : ''} ${className || ''}`}>
        <div className={`flex flex-col w-full ${pageTitle ? 'mt-6' : ''}`}>
          {showSubTitle && (
            <Text
              color="grey7"
              size="sm">
              {subTitle}
            </Text>
          )}
          {showTitle && (
            <div className={`flex items-${tabs ? 'start' : 'center'} space-x-2`}>
              <div className={`flex flex-${tabs ? 'col' : 'row space-x-2'}`}>
                <div className="flex items-center space-x-1">
                  {showIcon && icon && (
                    <Icon
                      data-testid="page-title-icon"
                      type={icon}
                    />
                  )}
                  <Text
                    color="grey9"
                    size="xl"
                    weight="medium">
                    {pageTitle}
                  </Text>
                  {showArchived && (
                    <Text
                      className="bg-grey-7 p-[1px] pl-[4.5px] pr-2 text-[10px] rounded-[4px] flex items-center !ml-3"
                      weight="medium"
                      color="white">
                      <Icon
                        type="alertWhite"
                        className="p-3"
                      />
                      Archived
                    </Text>
                  )}
                </div>
                <div className="flex items-center space-x-2">
                  {tabs && handleTabChange && (
                    <Tabs
                      value={activeTab}
                      onChange={(_e, index) => {
                        handleTabChange(index);
                      }}
                      slotProps={{
                        list: { className: `!grid grid-cols-${tabs.length} space-x-2` },
                        indicator: { className: '!hidden' }
                      }}
                      sx={{
                        minHeight: 'inherit',
                        '& .MuiTab-root': {
                          fontSize: '0.8rem',
                          textTransform: 'none',
                          color: '#000000',
                          borderRadius: '4px',
                          padding: '0.5rem 1.25rem',
                          minWidth: 'inherit',
                          minHeight: 'inherit',
                          fontWeight: 400,
                          backgroundColor: 'transparent',
                          border: 'none'
                        },
                        '& .MuiTab-root.Mui-selected': {
                          backgroundColor: '#e7e7e7',
                          color: '#000000'
                        },
                        '& .MuiTab-root:hover': {
                          backgroundColor: '#e5e7eb'
                        }
                      }}>
                      {tabs.map((tab) => (
                        <Tab
                          key={tab}
                          label={tab}
                          data-testid={`tab-${tab}`}
                        />
                      ))}
                    </Tabs>
                  )}
                  {shouldShowInternalLinks && (
                    <div className="flex items-center py-2">
                      <InternalLinks options={visibleInternalLinks} />
                    </div>
                  )}
                  {showLink && (
                    <div className="flex items-center p-[4px] border border-grey-3 rounded-md mt-[-1px]">
                      <Tooltip
                        title={tooltipLinkText}
                        placement="top">
                        <a
                          className="self-center"
                          data-testid="openInSalesforceButton"
                          href={link}
                          target="_blank"
                          rel="noreferrer">
                          <Icon type="external" />
                        </a>
                      </Tooltip>
                    </div>
                  )}
                  {showLinkGroup && (
                    <div className="flex items-center py-2">
                      <SalesforceLinks
                        sfLink={link}
                        options={linkGroup}
                      />
                    </div>
                  )}
                  {showCopy && (
                    <div className="flex items-center pr-2 py-2">
                      <CopyButton value={copyValue} />
                    </div>
                  )}
                </div>
              </div>
              {enableActions && actionLocation === 'top' && <div className="!ml-auto">{pageActions}</div>}
            </div>
          )}
        </div>
        {children}
        {enableActions && actionLocation === 'bottom' && <div className="mt-5">{pageActions}</div>}
      </div>
    </>
  );
};
